/**
 * Team Management → [Person]: the economics control centre. Configuration only — every save
 * writes into the canonical Step 9 objects; nothing here calculates money.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Card, StatusPill, btnGhost, btnPrimary, btnSmall, errorText, inputCls } from '../components/finance/financeUi';
import { ScheduleBuilder } from '../components/team-economics/ScheduleBuilder';
import { SplitTab } from '../components/team-economics/SplitTab';

const TABS = [['overview', 'Overview'], ['schedule', 'Schedule A'], ['split', 'Residual Split'], ['bonus', 'Bonus Program'],
              ['defaults', 'Downline Defaults'], ['sources', 'Report Sources'], ['history', 'History']];
const today = () => new Date().toISOString().slice(0, 10);
const post = (path, body, method = 'POST') => api.request(`/team-economics/${path}`, { method, body: JSON.stringify(body) });
const useLoad = (path) => {
  const [data, setData] = useState(null);
  const load = useCallback(() => { api.request(`/team-economics/${path}`).then(setData).catch((e) => { toast.error(errorText(e)); setData(null); }); }, [path]);
  useEffect(() => { load(); }, [load]);
  return [data, load];
};

function ScheduleTab({ uid, user, mscLabel, canEdit }) {
  const [data, load] = useLoad(`${uid}/schedule-a`);
  const [eff, setEff] = useState(today());
  const [reason, setReason] = useState('');
  const [building, setBuilding] = useState(false);
  if (!data) return null;
  const save = async (fn, ok) => { try { await fn(); toast.success(ok); load(); } catch (e) { toast.error(errorText(e)); } };
  return (
    <div className="space-y-5 px-5 py-4" data-testid="schedule-a-tab">
      <div className="grid gap-3 sm:grid-cols-3">
        <label className="text-sm"><span className="mb-1 block text-xs text-muted-foreground">Effective date</span>
          <input type="date" className={inputCls} value={eff} onChange={(e) => setEff(e.target.value)} data-testid="schedule-effective-date" /></label>
        <label className="text-sm"><span className="mb-1 block text-xs text-muted-foreground">Reason</span>
          <input className={inputCls} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. signed agreement" data-testid="schedule-reason" /></label>
        <label className="text-sm"><span className="mb-1 block text-xs text-muted-foreground">Schedule A template</span>
          <select className={inputCls} disabled={!canEdit} value={data.profile.schedule_template_id || ''} data-testid="schedule-template-select"
                  onChange={(e) => save(() => post(`${uid}/schedule-a`, { template_id: e.target.value, effective_from: eff, reason }, 'PUT'), 'Schedule A template set')}>
            <option value="" disabled>Choose the negotiated template…</option>
            {data.templates.map((t) => <option key={t.template_id} value={t.template_id}>{t.name}</option>)}
          </select>
        </label>
      </div>
      <p className="text-xs text-muted-foreground">Set the effective date and reason first — each template or platform change saves immediately with them.</p>
      {canEdit && !building && <button type="button" className={btnGhost} onClick={() => setBuilding(true)} data-testid="schedule-build-open-btn">Create Schedule A for a platform</button>}
      {building && <ScheduleBuilder uid={uid} user={user} platforms={data.platforms} templates={data.templates}
                                    onSaved={() => { setBuilding(false); load(); }} onCancel={() => setBuilding(false)} />}
      <table className="w-full text-sm" data-testid="platform-rules-grid">
        <thead className="text-left text-xs uppercase tracking-wide text-muted-foreground">
          <tr><th className="py-2">Report source / processing</th><th className="py-2">Rule</th><th className="py-2">Since</th></tr>
        </thead>
        <tbody>
          {data.platforms.map((p) => (
            <tr key={p.platform} className="border-t" data-testid={`platform-row-${p.platform}`}>
              <td className="py-2.5"><span className="font-medium">{p.residual_report_source}</span>
                <span className="block text-xs text-muted-foreground">{p.processing_platform || '—'}{p.economic_program ? ` · ${p.economic_program}` : ''}{p.cost_basis_ready ? '' : ' · cost basis pending'}</span></td>
              <td className="py-2.5">
                <select className={`${inputCls} sm:w-64`} disabled={!canEdit} value={p.mode || ''} data-testid={`platform-mode-${p.platform}`}
                        onChange={(e) => save(() => post(`${uid}/platform-rules/${p.platform}`, { mode: e.target.value, effective_from: eff, reason }, 'PUT'), `${p.label} rule saved`)}>
                  <option value="" disabled>Not set</option>
                  <option value="USE_MSC_COST">{mscLabel}</option>
                  <option value="APPLY_ASSIGNED_SCHEDULE">Apply Schedule A</option>
                </select>
                {p.start_method && p.mode === 'APPLY_ASSIGNED_SCHEDULE' && <span className="ml-2 text-xs text-muted-foreground">built · {p.start_method.replace(/_/g, ' ')}</span>}
                {p.pending_approval && <span className="ml-2 text-xs text-amber-700">awaiting approval</span>}
              </td>
              <td className="py-2.5 text-xs text-muted-foreground">{p.effective_from ? new Date(p.effective_from).toLocaleDateString() : '—'}</td>
            </tr>
          ))}
          {!data.platforms.length && <tr><td colSpan={3} className="py-6 text-center text-muted-foreground">No platform cost bases are active yet.</td></tr>}
        </tbody>
      </table>
      <p className="text-xs text-muted-foreground">Changes apply from the effective date. Earlier periods keep the rule that was in force then.</p>
    </div>
  );
}

function BonusTab({ uid, canEdit }) {
  const [rows, load] = useLoad(`${uid}/bonus-programs`);
  const [form, setForm] = useState({ program_type: 'production_bonus', basis: 'flat_amount', amount: '', name: '', criteria: '', effective_from: today() });
  const add = async () => {
    try { await post(`${uid}/bonus-programs`, { ...form, amount: Number(form.amount || 0) }); toast.success('Bonus program added'); load(); }
    catch (e) { toast.error(errorText(e)); }
  };
  return (
    <div className="space-y-4 px-5 py-4" data-testid="bonus-tab">
      {canEdit && (
        <div className="grid gap-2 sm:grid-cols-6">
          <select className={inputCls} value={form.program_type} onChange={(e) => setForm({ ...form, program_type: e.target.value })} data-testid="bonus-type-select">
            {[['production_bonus', 'Production Bonus'], ['volume_bonus', 'Volume Bonus'], ['new_mid_bonus', 'New MID Bonus'], ['temporary_promotion', 'Temporary Promotion']].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <input className={inputCls} placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="bonus-name-input" />
          <select className={inputCls} value={form.basis} onChange={(e) => setForm({ ...form, basis: e.target.value })} data-testid="bonus-basis-select">
            {['flat_amount', 'per_merchant', 'percent_of_residual', 'split_points'].map((t) => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
          </select>
          <input type="number" className={inputCls} placeholder="Amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="bonus-amount-input" />
          <input className={inputCls} placeholder="Criteria" value={form.criteria} onChange={(e) => setForm({ ...form, criteria: e.target.value })} data-testid="bonus-criteria-input" />
          <button type="button" className={btnPrimary} onClick={add} data-testid="bonus-add-btn">Add</button>
        </div>
      )}
      <ul className="divide-y text-sm">
        {(rows || []).map((b) => (
          <li key={b.bonus_id} className="flex items-center justify-between py-2.5" data-testid={`bonus-row-${b.bonus_id}`}>
            <span>{b.name} · {b.basis.replace(/_/g, ' ')} {b.amount} {b.criteria ? `· ${b.criteria}` : ''}</span>
            <span className="flex items-center gap-2"><StatusPill value={b.status.toUpperCase()} />
              {canEdit && b.status === 'active' && <button type="button" className={btnSmall} data-testid={`bonus-end-${b.bonus_id}`}
                onClick={() => post(`${uid}/bonus-programs/${b.bonus_id}/end`, { reason: 'Ended in Team Management' }).then(load).catch((e) => toast.error(errorText(e)))}>End</button>}
            </span>
          </li>
        ))}
      </ul>
      <p className="text-xs text-muted-foreground">Bonuses are configuration. Any payment goes through the Step 9 payout adjustment layer.</p>
    </div>
  );
}

function DefaultsTab({ uid, user, upline, canApply, onApplied }) {
  const [defaults, load] = useLoad(`${uid}/downline-defaults`);
  const [templates, setTemplates] = useState([]);
  useEffect(() => {
    api.request(`/team-economics/catalog/schedule-templates?tenant_id=${user.tenant_id || ''}`).then(setTemplates).catch(() => setTemplates([]));
  }, [user.tenant_id]);
  const [plans, setPlans] = useState([]);
  const [form, setForm] = useState(null);
  useEffect(() => { api.request('/team-economics/catalog/plan-templates').then(setPlans).catch(() => setPlans([])); }, []);
  useEffect(() => { if (defaults) setForm({ agent_template_id: '', agent_split_percent: '', agent_plan_template_id: '', subagent_template_id: '', subagent_split_percent: '', subagent_plan_template_id: '', ...defaults }); }, [defaults]);
  if (!form) return null;
  const save = async () => {
    const num = (v) => (v === '' || v == null ? null : Number(v));
    try {
      await post(`${uid}/downline-defaults`, { ...form, agent_split_percent: num(form.agent_split_percent), subagent_split_percent: num(form.subagent_split_percent),
        agent_template_id: form.agent_template_id || null, subagent_template_id: form.subagent_template_id || null,
        agent_plan_template_id: form.agent_plan_template_id || null, subagent_plan_template_id: form.subagent_plan_template_id || null }, 'PUT');
      toast.success('Downline defaults saved — existing downline is unchanged'); load();
    } catch (e) { toast.error(errorText(e)); }
  };
  const apply = async () => {
    try {
      const r = await post(`${uid}/apply-defaults`, { effective_from: today() });
      toast.success(`Defaults applied (${r.platform_rules.length} platform rule(s))`);
      (r.warnings || []).forEach((w) => toast.warning(w));
      onApplied();
    } catch (e) { toast.error(errorText(e)); }
  };
  const tpl = (key) => (
    <select className={inputCls} value={form[key] || ''} onChange={(e) => setForm({ ...form, [key]: e.target.value })} data-testid={`defaults-${key}`}>
      <option value="">None</option>
      {templates.filter((t) => !t.target_role || key.startsWith(t.target_role)).map((t) => <option key={t.template_id} value={t.template_id}>{t.name}</option>)}
    </select>
  );
  const plan = (key) => (
    <select className={inputCls} value={form[key] || ''} onChange={(e) => setForm({ ...form, [key]: e.target.value })} data-testid={`defaults-${key}`}>
      <option value="">No tier plan (use flat split)</option>
      {plans.map((p) => <option key={p.template_id} value={p.template_id}>{p.name}</option>)}
    </select>
  );
  return (
    <div className="space-y-4 px-5 py-4" data-testid="downline-defaults-tab">
      {['iso', 'iso_agent'].includes(user.role) && (
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="text-sm"><span className="mb-1 block text-xs text-muted-foreground">Default agent Schedule A</span>{tpl('agent_template_id')}</label>
          <label className="text-sm"><span className="mb-1 block text-xs text-muted-foreground">Default agent split %</span>
            <input type="number" className={inputCls} value={form.agent_split_percent ?? ''} onChange={(e) => setForm({ ...form, agent_split_percent: e.target.value })} data-testid="defaults-agent-split" /></label>
          <label className="text-sm"><span className="mb-1 block text-xs text-muted-foreground">Default agent tier plan</span>{plan('agent_plan_template_id')}</label>
          <label className="text-sm"><span className="mb-1 block text-xs text-muted-foreground">Default subagent Schedule A</span>{tpl('subagent_template_id')}</label>
          <label className="text-sm"><span className="mb-1 block text-xs text-muted-foreground">Default subagent split %</span>
            <input type="number" className={inputCls} value={form.subagent_split_percent ?? ''} onChange={(e) => setForm({ ...form, subagent_split_percent: e.target.value })} data-testid="defaults-subagent-split" /></label>
          <label className="text-sm"><span className="mb-1 block text-xs text-muted-foreground">Default subagent tier plan</span>{plan('subagent_plan_template_id')}</label>
          <button type="button" className={btnPrimary} onClick={save} data-testid="defaults-save-btn">Save defaults</button>
        </div>
      )}
      {upline && canApply && (
        <div className="rounded-xl border p-4 text-sm">
          <p>Copy {upline.name}'s current defaults onto {user.first_name}. Later changes to {upline.name}'s defaults won't affect this copy.</p>
          <button type="button" className={`${btnGhost} mt-3`} onClick={apply} data-testid="apply-upline-defaults-btn">Apply {upline.name}'s Defaults</button>
        </div>
      )}
    </div>
  );
}

function SourcesTab({ uid }) {
  const [rows] = useLoad(`${uid}/report-sources`);
  return (
    <ul className="divide-y text-sm" data-testid="report-sources-tab">
      {(rows || []).map((p) => (
        <li key={p.platform} className="grid gap-1 px-5 py-3 sm:grid-cols-4" data-testid={`report-source-${p.platform}`}>
          <span><span className="block text-[11px] uppercase tracking-wide text-muted-foreground">Residual report source</span><span className="font-medium">{p.residual_report_source}</span></span>
          <span><span className="block text-[11px] uppercase tracking-wide text-muted-foreground">Processing platform</span>{p.processing_platform || '—'}</span>
          <span className="text-xs text-muted-foreground sm:self-end">{p.cost_basis_ready ? 'Cost basis active' : 'Cost basis pending'} · {p.connections.length} connection(s)</span>
          <span className="text-xs text-muted-foreground sm:self-end">{p.mode ? (p.mode === 'USE_MSC_COST' ? 'at upline cost' : 'assigned schedule') : 'no rule'}</span>
        </li>
      ))}
    </ul>
  );
}

function HistoryTab({ uid }) {
  const [rows] = useLoad(`${uid}/history`);
  const fmt = (v) => {
    if (v == null || v === '') return '—';
    if (Array.isArray(v)) return v.map((t) => (t && t.split_percent != null ? `${t.split_percent}%` : fmt(t))).join(' / ');
    if (typeof v !== 'object') return String(v).replace(/_/g, ' ');
    return Object.entries(v).filter(([k, x]) => x != null && !/(_id|^id|version)$/.test(k))
      .map(([k, x]) => `${k.replace(/_/g, ' ')}: ${fmt(x)}`).join(' · ') || '—';
  };
  return (
    <ol className="divide-y text-xs" data-testid="economics-history-tab">
      {(rows || []).map((c) => (
        <li key={c.change_id} className="grid gap-1 px-5 py-3 sm:grid-cols-[10rem_9rem_1fr]">
          <span className="text-muted-foreground">{new Date(c.timestamp).toLocaleString()}</span>
          <span className="font-medium">{c.area.replace(/_/g, ' ')}</span>
          <span className="break-all">{fmt(c.previous_value)} → {fmt(c.new_value)}<span className="block text-muted-foreground">
            by {c.changed_by_name || c.changed_by} · effective {c.effective_date ? String(c.effective_date).slice(0, 10) : '—'} · {c.source}{c.reason ? ` · ${c.reason}` : ''}</span></span>
        </li>
      ))}
      {rows && !rows.length && <li className="px-5 py-10 text-center text-muted-foreground">No economics changes yet.</li>}
    </ol>
  );
}

export default function TeamMemberEconomicsPage() {
  const { userId } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = TABS.some(([k]) => k === params.get('tab')) ? params.get('tab') : 'overview';
  const navigate = useNavigate();
  const [data, reload] = useLoad(userId);
  if (!data) return <div className="p-10 text-sm text-muted-foreground" data-testid="team-economics-loading">Loading…</div>;
  const u = data.user;
  const mscLabel = data.upline_is_msc ? 'Use MSC Cost' : 'Use Upline Cost';
  return (
    <div className="space-y-6" data-testid="team-economics-page">
      <button type="button" onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:underline" data-testid="team-back-btn"><ArrowLeft className="h-4 w-4" />Back</button>
      <header>
        <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">Team Management</p>
        <h1 className="mt-1 text-4xl font-semibold tracking-tight" data-testid="team-member-name">{u.first_name} {u.last_name}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{String(u.role).replace(/_/g, ' ')} · {u.status}{data.upline ? ` · under ${data.upline.name}` : ''}</p>
      </header>
      <div className="flex flex-wrap gap-2" data-testid="team-economics-tabs">
        {TABS.map(([k, l]) => (
          <button key={k} type="button" onClick={() => setParams({ tab: k })} data-testid={`team-tab-${k}`}
                  className={`h-9 rounded-full px-4 text-sm transition-colors ${tab === k ? 'bg-primary text-primary-foreground' : 'border hover:bg-muted'}`}>{l}</button>
        ))}
      </div>
      <Card title={TABS.find(([k]) => k === tab)[1]}>
        {tab === 'overview' && (
          <dl className="grid gap-4 px-5 py-4 sm:grid-cols-3" data-testid="team-overview-tab">
            {[['Schedule A', data.schedule_template_name], ['Platforms marked up', data.rules.APPLY_ASSIGNED_SCHEDULE],
              [`Platforms on ${mscLabel.replace('Use ', '')}`, data.rules.USE_MSC_COST],
              ['Residual split', data.split?.schedule_a?.compensation_type === 'tiered' ? `Tiered · ${data.split.schedule_a.tier_mode === 'PROSPECTIVE_NEW_ACCOUNTS' ? 'prospective' : 'retroactive'}` : (data.split?.schedule_a?.split_percent != null ? `${data.split.schedule_a.split_percent}%` : null)],
              ['Active bonuses', data.active_bonuses], ['Downline defaults', data.has_downline_defaults ? 'Set' : 'Not set']].map(([k, v]) => (
              <div key={k}><dt className="text-[11px] uppercase tracking-wide text-muted-foreground">{k}</dt><dd className="mt-0.5 text-sm font-medium">{v ?? '—'}</dd></div>
            ))}
          </dl>
        )}
        {tab === 'schedule' && <ScheduleTab uid={userId} user={u} mscLabel={mscLabel} canEdit={data.can_edit} />}
        {tab === 'split' && <SplitTab uid={userId} split={data.split} canEdit={data.can_edit} onSaved={reload} />}
        {tab === 'bonus' && <BonusTab uid={userId} canEdit={data.can_edit} />}
        {tab === 'defaults' && <DefaultsTab uid={userId} user={u} upline={data.upline} canApply={data.can_edit} onApplied={reload} />}
        {tab === 'sources' && <SourcesTab uid={userId} />}
        {tab === 'history' && <HistoryTab uid={userId} />}
      </Card>
    </div>
  );
}
